import { Banner, DataTable, Text } from '@dixa/lib-shared-habla';

import { companiesApi } from 'apis/companies';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { getUserDisplayName, userTypeAssets } from 'utils/user';

import { ContactsTableUser } from '../../../contactsTableUser/contactsTableUser';

interface CompanyRelatedUsersProps {
  companyId: string;
}

export const CompanyRelatedUsers = ({
  companyId,
}: CompanyRelatedUsersProps): JSX.Element => {
  const {
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    items: users,
    total,
    asyncStatus,
  } = usePaginatedEndpoint({
    endpoint: companiesApi.endpoints.readCompanyRelatedUsers,
    initialArg: { id: companyId, limit: 25, offset: 0 },
    totalSelector: ({ data: { users } }) => users.length,
    itemsSelector: ({ data: { users } }) => users,
  });
  const isLoading = asyncStatus === 'pending';
  const isSuccess = asyncStatus === 'fulfilled';

  if (isLoading && !total) {
    return <Text.Paragraph>Loading related users...</Text.Paragraph>;
  }

  if (asyncStatus === 'rejected') {
    <Banner
      variant="fail"
      icon={null}
      description={
        <Text.Paragraph>
          An error occurred while loading related users
        </Text.Paragraph>
      }
    />;
  }

  if (!total) {
    return (
      <Banner
        variant="info"
        icon={null}
        description={
          <Text.Paragraph>
            There are no related users for this company
          </Text.Paragraph>
        }
      />
    );
  }

  return (
    <DataTable
      data={users}
      columns={[
        {
          label: 'User',
          selector: (user) => (
            <ContactsTableUser
              name={getUserDisplayName(user)}
              avatarUser={user}
            />
          ),
        },
        { label: 'Email', selector: ({ email }) => email },
        { label: 'Phone number', selector: ({ phone_number }) => phone_number },
        {
          label: 'User category',
          selector: ({ type }) => userTypeAssets[type]?.prettyName,
        },
      ]}
      paginationProps={{
        loading: isLoading,
        previousButtonDisabled: !hasPreviousPage || !isSuccess,
        nextButtonDisabled: !hasNextPage || !isSuccess,
        nextButtonClick: nextPage,
        previousButtonClick: previousPage,
      }}
    />
  );
};
